import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../routes';

const SITE_NAME = 'PH4SM4 SOLUTIONS';
const BRAND_SUFFIX = 'Olaf Dalach - PH4SM4 SOLUTIONS';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const DEFAULT_DESCRIPTION =
	'Olaf Dalach - Full-Stack Developer with 5+ years of experience in programming. Specializing in Websites, Scripts, Discord Bots, and Desktop Apps.';

/**
 * Stable @id anchors. Every page references these same nodes rather than
 * redefining the organization/person, so Google resolves one entity for each
 * across the whole site instead of a dozen near-duplicates.
 */
export const ORG_ID = `${SITE_URL}/#organization`;
export const PERSON_ID = `${SITE_URL}/#olaf-dalach`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const SAME_AS = [
	'https://github.com/Ph4sm4',
	'https://www.linkedin.com/in/olaf-dalach/'
];

/** The site-wide entities, emitted on every page. */
const baseGraph = [
	{
		'@type': 'Organization',
		'@id': ORG_ID,
		name: SITE_NAME,
		url: `${SITE_URL}/`,
		logo: {
			'@type': 'ImageObject',
			url: `${SITE_URL}/logo192.png`
		},
		founder: { '@id': PERSON_ID },
		sameAs: SAME_AS
	},
	{
		'@type': 'Person',
		'@id': PERSON_ID,
		name: 'Olaf Dalach',
		jobTitle: 'Full-Stack Developer',
		url: `${SITE_URL}/`,
		email: 'mailto:ph4sm4solutions@gmail.com',
		worksFor: { '@id': ORG_ID },
		knowsAbout: [
			'Qt C++',
			'C++',
			'React.js',
			'TypeScript',
			'Node.js',
			'C#',
			'Discord Bots',
			'Desktop Application Development',
			'Web Development'
		],
		sameAs: SAME_AS
	},
	{
		'@type': 'WebSite',
		'@id': WEBSITE_ID,
		url: `${SITE_URL}/`,
		name: BRAND_SUFFIX,
		description: DEFAULT_DESCRIPTION,
		publisher: { '@id': ORG_ID },
		inLanguage: 'en'
	}
];

type Props = {
	/** Page title without the brand suffix. */
	title: string;
	description?: string;
	/** Absolute path beginning with "/" used for canonical + og:url. */
	path?: string;
	image?: string;
	/** When true, prevent indexing (used for private/personal pages). */
	noIndex?: boolean;
	/** "website" for the home page, "article" for project pages. */
	ogType?: 'website' | 'article';
	/**
	 * Page-specific JSON-LD nodes appended to the site-wide @graph. Reference the
	 * shared entities by @id (ORG_ID / PERSON_ID) rather than re-declaring them.
	 */
	jsonLd?: Record<string, unknown>[];
};

export default function Seo({
	title,
	description = DEFAULT_DESCRIPTION,
	path = '/',
	image = DEFAULT_IMAGE,
	noIndex = false,
	ogType = 'website',
	jsonLd = []
}: Props) {
	const fullTitle = title.includes(SITE_NAME)
		? title
		: `${title} | ${BRAND_SUFFIX}`;
	const url = `${SITE_URL}${path}`;

	// Private pages are excluded from the sitemap and robots.txt already; there is
	// no reason to hand crawlers structured data for them either.
	const graph = noIndex ? [] : [...baseGraph, ...jsonLd];

	return (
		<Helmet prioritizeSeoTags>
			<html lang="en" />
			<title>{fullTitle}</title>
			<meta name="description" content={description} />
			<link rel="canonical" href={url} />
			<meta
				name="robots"
				content={noIndex ? 'noindex, nofollow' : 'index, follow'}
			/>

			<meta property="og:type" content={ogType} />
			<meta property="og:site_name" content={SITE_NAME} />
			<meta property="og:locale" content="en_US" />
			<meta property="og:title" content={fullTitle} />
			<meta property="og:description" content={description} />
			<meta property="og:url" content={url} />
			<meta property="og:image" content={image} />
			<meta property="og:image:width" content="1200" />
			<meta property="og:image:height" content="630" />
			<meta property="og:image:alt" content={fullTitle} />

			<meta name="twitter:card" content="summary_large_image" />
			<meta name="twitter:title" content={fullTitle} />
			<meta name="twitter:description" content={description} />
			<meta name="twitter:image" content={image} />

			{graph.length > 0 && (
				<script type="application/ld+json">
					{JSON.stringify({
						'@context': 'https://schema.org',
						'@graph': graph
					})}
				</script>
			)}
		</Helmet>
	);
}
